/**
 * /eval · M07 批量回归（S2 Day 8b · T11 §3.2 路由 → M07）
 *
 * 单页：意图 / 方案命中率卡片 + 混淆矩阵热力图 + 单样本失败定位
 * 数据：confused after Day 8.5 baseline · 36 金样本 · 不依赖 LLM 的确定性回归
 */
import { createFileRoute, useRouterState } from "@tanstack/react-router";

import { M07Shell, type M07View } from "@/components/m07/m07-shell";
import { PageHeader, type PageHelp } from "@/components/shared/page-help";

export const Route = createFileRoute("/eval")({
	component: EvalPage,
});

const EVAL_VIEW_COPY: Record<
	M07View,
	{
		title: string;
		description: string;
		help: PageHelp;
	}
> = {
	"scenario-metrics": {
		title: "场景指标",
		description: "按业务场景定义效果目标和判断标准。",
		help: {
			title: "场景指标说明",
			description: "这里回答“这轮优化到底要让哪类业务结果变好”。",
			sections: [
				{
					title: "这里做什么",
					items: [
						"按售后办理、售前导购、弱意图澄清和高风险动作拆目标。",
						"把北极星指标、样本覆盖和判断口径放在一起看。",
						"为后续评测运行和实验治理提供业务目标。",
					],
				},
			],
		},
	},
	"eval-run": {
		title: "评测运行",
		description: "运行回归任务，查看批量结果和单环节候选。",
		help: {
			title: "评测运行说明",
			description: "这里回答“本次改动跑出来的结果是否可信”。",
			sections: [
				{
					title: "这里做什么",
					items: [
						"发起意图、方案或端到端回归。",
						"查看运行历史、命中率、混淆矩阵和失败样本。",
						"承接从链路测试台带入的单环节人工候选。",
					],
				},
				{
					title: "不要误读",
					items: [
						"单环节候选不是自动问题样本，也不是自动回归写回。",
						"mock 运行只能证明模拟数据口径，不能代表真实 Shopify 已接通。",
					],
				},
			],
		},
	},
	"cx-score": {
		title: "体验打分(L0)",
		description: "对话级 LLM judge 给完整客服对话打 CX Score（1–5 分 + 7 reasons）。",
		help: {
			title: "L0 CX Score 说明",
			description: "这里回答“这通对话的整体客户体验到底好不好”。",
			sections: [
				{
					title: "这里做什么",
					items: [
						"输入/粘贴一段完整客服对话，运行 judge 打分。",
						"看 cx_score(1–5) + 7 条 reasons + 中文解释。",
						"Compliance=High（发生越权/错报）会硬封顶 ≤2。",
					],
				},
				{
					title: "不要误读",
					items: [
						"需至少 1 轮用户 + 1 轮客服才评分；否则返回“未评分”。",
						"judge 走真 LLM，失败/超时按“未评分”处理，不污染结论。",
					],
				},
			],
		},
	},
	"badcase-loop": {
		title: "问题闭环",
		description: "确认问题样本、复跑、写回回归集并关闭问题。",
		help: {
			title: "问题闭环说明",
			description: "这里回答“失败样本怎么被确认、修复和防回归”。",
			sections: [
				{
					title: "这里做什么",
					items: [
						"把候选问题样本确认为真实问题或关闭。",
						"登记复跑结果，把已修复样本写回回归集。",
						"生成批量回归任务并批量关闭符合条件的问题。",
					],
				},
			],
		},
	},
	"experiment-governance": {
		title: "实验治理",
		description: "沉淀 baseline/current 对比、资产责任链和实验结论。",
		help: {
			title: "实验治理说明",
			description: "这里回答“这次优化是否值得进入下一步发布评估”。",
			sections: [
				{
					title: "这里做什么",
					items: [
						"查看评测运行、数据集版本、资产版本和责任资产。",
						"保存 baseline/current 对比快照。",
						"用可追溯、可对比、可审核、可验收的标准整理实验结论。",
					],
				},
				{
					title: "当前边界",
					items: [
						"当前只做离线实验治理，不启动线上真实流量分流。",
						"线上候选发布和审核队列仍是后续切片。",
					],
				},
			],
		},
	},
};

function EvalPage() {
	const view = getEvalView(useRouteHash());
	const copy = EVAL_VIEW_COPY[view];

	return (
		<div id={viewToHash(view)} className="scroll-mt-6 space-y-4">
			<PageHeader title={copy.title} description={copy.description} help={copy.help} />
			<M07Shell view={view} />
		</div>
	);
}

function useRouteHash() {
	const routeHash = useRouterState({ select: (state) => state.location.hash });
	if (routeHash) return routeHash;
	if (typeof window === "undefined") return "";
	return window.location.hash;
}

function getEvalView(hash: string): M07View {
	const normalized = hash.replace(/^#/, "");
	if (normalized === "scenario-metrics") return "scenario-metrics";
	if (normalized === "cx-score") return "cx-score";
	if (normalized === "eval-badcase-loop") return "badcase-loop";
	if (normalized === "ab-experiment-governance") return "experiment-governance";
	return "eval-run";
}

function viewToHash(view: M07View): string {
	if (view === "scenario-metrics") return "scenario-metrics";
	if (view === "cx-score") return "cx-score";
	if (view === "badcase-loop") return "eval-badcase-loop";
	if (view === "experiment-governance") return "ab-experiment-governance";
	return "eval-run";
}
